import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import { Star, Check, EyeSlash, ChatCircleDots, ArrowClockwise } from "@phosphor-icons/react";

const STATUS_META = {
    pending: { label: "Pending", cls: "bg-[#FEF3C7] text-[#B45309]" },
    approved: { label: "Approved", cls: "bg-[#ECFDF5] text-[#059669]" },
    hidden: { label: "Hidden", cls: "bg-[#F5F5F5] text-[#525252]" },
};

export default function TestimonialsModeration() {
    const [data, setData] = useState(null);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [busy, setBusy] = useState(null);

    const load = useCallback(() => {
        const q = statusFilter === "all" ? "" : `?status=${statusFilter}`;
        api
            .get(`/testimonials${q}`)
            .then((r) => setData(r.data))
            .catch((e) => logError("Moderation load failed:", e));
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const moderate = async (id, status) => {
        setBusy(id + status);
        try {
            await api.post(`/testimonials/${id}/moderate`, { status });
            toast.success(status === "approved" ? "Testimonial approved" : "Testimonial hidden");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not update");
        } finally {
            setBusy(null);
        }
    };

    if (!data) return <div className="p-10 text-[#525252]">Loading…</div>;

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1200px] mx-auto" data-testid="testimonial-moderation-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Trust &amp; social proof
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink">
                        Testimonials.
                    </h1>
                </div>
                <div className="flex gap-2 self-start sm:self-auto">
                    {["pending", "approved", "hidden", "all"].map((s) => {
                        const count = s === "all" ? data.items.length : data.totals[s];
                        return (
                            <button
                                key={s}
                                onClick={() => setStatusFilter(s)}
                                data-testid={`testimonial-filter-${s}`}
                                className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                                    statusFilter === s
                                        ? "bg-ink text-white"
                                        : "bg-white border border-[#E5E5E5] text-[#525252] hover:border-ink"
                                }`}
                            >
                                {s}
                                {typeof count === "number" && (
                                    <span className="text-[10px] opacity-70">{count}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {data.items.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] p-10 text-center" data-testid="testimonial-empty">
                    <ChatCircleDots size={30} weight="bold" className="text-[#A3A3A3] mx-auto mb-3" />
                    <p className="text-sm text-[#525252]">
                        No {statusFilter !== "all" ? statusFilter : ""} testimonials yet.
                        Hired candidates can leave a review on their portal.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {data.items.map((t) => {
                        const meta = STATUS_META[t.status] || STATUS_META.pending;
                        return (
                            <article
                                key={t.id}
                                data-testid={`testimonial-row-${t.id}`}
                                className="bg-white border border-[#E5E5E5] p-5 sm:p-6"
                            >
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                                            {t.client_name}
                                        </p>
                                        <p className="font-heading text-lg font-bold tracking-tight text-ink truncate">
                                            {t.candidate_name}
                                            {t.anonymous && (
                                                <span className="text-[10px] font-normal text-[#A3A3A3] ml-2 uppercase tracking-wider">
                                                    (wants anonymous)
                                                </span>
                                            )}
                                        </p>
                                        {t.candidate_title && (
                                            <p className="text-xs text-[#525252]">as {t.candidate_title}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 ${meta.cls}`}>
                                            {meta.label}
                                        </span>
                                        <div className="flex gap-0.5">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Star
                                                    key={n}
                                                    size={12}
                                                    weight="fill"
                                                    className={n <= t.rating ? "text-[#F59E0B]" : "text-[#E5E5E5]"}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <p className="text-sm text-[#262626] leading-relaxed mb-4">
                                    &ldquo;{t.text}&rdquo;
                                </p>
                                <div className="flex items-center justify-between pt-3 border-t border-[#F5F5F5]">
                                    <p className="text-[11px] text-[#A3A3A3]">
                                        {new Date(t.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                                    </p>
                                    <div className="flex gap-2">
                                        {t.status !== "approved" && (
                                            <button
                                                onClick={() => moderate(t.id, "approved")}
                                                disabled={busy === t.id + "approved"}
                                                data-testid={`testimonial-approve-${t.id}`}
                                                className="btn-gradient px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
                                            >
                                                <Check size={11} weight="bold" /> Approve
                                            </button>
                                        )}
                                        {t.status !== "hidden" && (
                                            <button
                                                onClick={() => moderate(t.id, "hidden")}
                                                disabled={busy === t.id + "hidden"}
                                                data-testid={`testimonial-hide-${t.id}`}
                                                className="border border-[#E5E5E5] hover:border-[#DC2626] hover:text-[#DC2626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
                                            >
                                                <EyeSlash size={11} weight="bold" /> Hide
                                            </button>
                                        )}
                                        {t.status === "hidden" && (
                                            <button
                                                onClick={() => moderate(t.id, "pending")}
                                                disabled={busy === t.id + "pending"}
                                                data-testid={`testimonial-restore-${t.id}`}
                                                className="border border-[#E5E5E5] hover:border-ink px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
                                            >
                                                <ArrowClockwise size={11} weight="bold" /> Restore
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
